import dumpPipelineApi from './DumpPipelineAxios';

function is404(err) {
    return err?.response?.status === 404;
}

let validationApiMode = 'auto'; // auto | new | legacy

function isLegacyMode() {
    return validationApiMode === 'legacy';
}

function markLegacy() {
    validationApiMode = 'legacy';
}

function markNew() {
    validationApiMode = 'new';
}

function mapLegacyJob(job) {
    const statusRaw = job?.status || job?.Status || 'concluido';
    const statusMap = {
        em_processamento: 'running',
        concluido: 'completed',
        erro: 'error',
    };
    return {
        ...job,
        id: job?.id || job?._id || job?.ID || '',
        status: statusMap[statusRaw] || statusRaw,
        total: Number(job?.total ?? job?.Total ?? 0),
        processados: Number(job?.processados ?? job?.Processados ?? 0),
        validos: Number(job?.validos ?? job?.Validos ?? 0),
        invalidos: Number(job?.invalidos ?? job?.Invalidos ?? 0),
        createdAt: job?.createdAt || job?.criadoEm || job?.CriadoEm,
        updatedAt: job?.updatedAt || job?.concluidoEm || job?.ConcluidoEm || job?.criadoEm || job?.CriadoEm,
    };
}

async function listLegacyJobs() {
    const { data } = await dumpPipelineApi.get('/validar/jobs');
    return Array.isArray(data) ? data.map(mapLegacyJob) : [];
}

export async function getValidationStatus() {
    if (isLegacyMode()) {
        const jobs = await listLegacyJobs();
        const latest = jobs.length ? jobs[0] : null;
        const hasActive = jobs.some((j) => ['running', 'pauseRequested', 'paused'].includes(j?.status));
        return {
            hasValidation: jobs.length > 0,
            hasActive,
            latest,
            compatibilityMode: 'legacy-validar',
        };
    }

    try {
        const { data } = await dumpPipelineApi.get('/validation/status');
        markNew();
        return data;
    } catch (err) {
        if (!is404(err)) throw err;
        markLegacy();
        const jobs = await listLegacyJobs();
        const latest = jobs.length ? jobs[0] : null;
        const hasActive = jobs.some((j) => ['running', 'pauseRequested', 'paused'].includes(j?.status));
        return {
            hasValidation: jobs.length > 0,
            hasActive,
            latest,
            compatibilityMode: 'legacy-validar',
        };
    }
}

export async function listValidationJobs() {
    if (isLegacyMode()) {
        return listLegacyJobs();
    }

    try {
        const { data } = await dumpPipelineApi.get('/validation');
        markNew();
        return data;
    } catch (err) {
        if (!is404(err)) throw err;
        markLegacy();
        return listLegacyJobs();
    }
}

export async function startValidationGlobal(payload = {}) {
    if (isLegacyMode()) {
        const legacyPayload = {};
        if (payload?.since) legacyPayload.since = payload.since;
        const { data } = await dumpPipelineApi.post('/validar/dump-job', legacyPayload);
        return data;
    }

    try {
        const { data } = await dumpPipelineApi.post('/validation', payload);
        markNew();
        return data;
    } catch (err) {
        if (!is404(err)) throw err;
        markLegacy();
        const legacyPayload = {};
        if (payload?.since) legacyPayload.since = payload.since;
        const { data } = await dumpPipelineApi.post('/validar/dump-job', legacyPayload);
        return data;
    }
}

export async function pauseValidation(jobId) {
    if (isLegacyMode()) {
        throw new Error('Pausa não disponível no backend legado (/validar).');
    }

    try {
        const { data } = await dumpPipelineApi.post(`/validation/${jobId}/pause`);
        markNew();
        return data;
    } catch (err) {
        if (!is404(err)) throw err;
        markLegacy();
        throw new Error('Pausa não disponível no backend legado (/validar).');
    }
}

export async function resumeValidation(jobId) {
    if (isLegacyMode()) {
        throw new Error('Retomada não disponível no backend legado (/validar).');
    }

    try {
        const { data } = await dumpPipelineApi.post(`/validation/${jobId}/resume`);
        markNew();
        return data;
    } catch (err) {
        if (!is404(err)) throw err;
        markLegacy();
        throw new Error('Retomada não disponível no backend legado (/validar).');
    }
}

export async function deleteValidation(jobId) {
    if (isLegacyMode()) {
        throw new Error('Exclusão não disponível no backend legado (/validar).');
    }

    try {
        const { data } = await dumpPipelineApi.delete(`/validation/${jobId}`);
        markNew();
        return data;
    } catch (err) {
        if (!is404(err)) throw err;
        markLegacy();
        throw new Error('Exclusão não disponível no backend legado (/validar).');
    }
}

export async function getValidationLogs(jobId) {
    if (isLegacyMode()) {
        return [];
    }

    try {
        const { data } = await dumpPipelineApi.get(`/validation/${jobId}/logs`);
        markNew();
        return data;
    } catch (err) {
        if (!is404(err)) throw err;
        markLegacy();
        return [];
    }
}

export async function validateOne(payload) {
    if (isLegacyMode()) {
        const fallbackBody = {
            experimentId: payload?.experimentId || payload?.amostraId,
        };
        const { data } = await dumpPipelineApi.post('/validar', fallbackBody);
        return data;
    }

    try {
        const { data } = await dumpPipelineApi.post('/validation/one', payload);
        markNew();
        return data;
    } catch (err) {
        if (!is404(err)) throw err;
        markLegacy();
        const fallbackBody = {
            experimentId: payload?.experimentId || payload?.amostraId,
        };
        const { data } = await dumpPipelineApi.post('/validar', fallbackBody);
        return data;
    }
}

export async function validateAnalysis(payload) {
    if (isLegacyMode()) {
        const fallbackBody = {
            analysisId: payload?.analysisId,
        };
        if (!fallbackBody.analysisId && payload?.amostraId) {
            throw new Error('No backend legado, validação por análise exige analysisId.');
        }
        const { data } = await dumpPipelineApi.post('/validar/analise', fallbackBody);
        return data;
    }

    try {
        const { data } = await dumpPipelineApi.post('/validation/analysis', payload);
        markNew();
        return data;
    } catch (err) {
        if (!is404(err)) throw err;
        markLegacy();
        const fallbackBody = {
            analysisId: payload?.analysisId,
        };
        if (!fallbackBody.analysisId && payload?.amostraId) {
            throw new Error('No backend legado, validação por análise exige analysisId.');
        }
        const { data } = await dumpPipelineApi.post('/validar/analise', fallbackBody);
        return data;
    }
}

export async function listValidationExperiments(params = {}) {
    if (isLegacyMode()) {
        return [];
    }

    try {
        const { data } = await dumpPipelineApi.get('/validation/experiments', { params });
        markNew();
        return data;
    } catch (err) {
        if (!is404(err)) throw err;
        markLegacy();
        return [];
    }
}

export async function getValidationExperimentDetail(experimentId, params = {}) {
    if (!experimentId) {
        throw new Error('experimentId é obrigatório');
    }

    if (isLegacyMode()) {
        return null;
    }

    try {
        const { data } = await dumpPipelineApi.get(`/validation/experiments/${encodeURIComponent(experimentId)}`, { params });
        markNew();
        return data;
    } catch (err) {
        if (!is404(err)) throw err;
        return null;
    }
}
